import {
	ChangeDetectionStrategy,
	Component,
	ContentChild,
	EventEmitter,
	HostListener,
	Input,
	Output,
	TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	MatTreeFlatDataSource,
	MatTreeFlattener,
	MatTreeModule,
} from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { IClassification } from '@app/pages/cmms/interfaces/classification.interfaces';
import { BaseTreeControl, FlatTreeControl } from '@angular/cdk/tree';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject } from 'rxjs';
import { decrementIndex, incrementIndex } from '@app/core/utils/list-navigation-utils';

type FlatNode<T> = T & {
	level: number;
	expandable: boolean;
};

type TreeData<T> = T & {
	children: TreeData<T>[];
};
type TemplateRefPropsType = {
	$implicit: IClassification;
	treeControl: BaseTreeControl<IClassification>;
};
@Component({
	selector: 'classification-tree-component',
	imports: [CommonModule, MatTreeModule, MatIconModule, MatButtonModule],
	templateUrl: './classification-tree-component.component.html',
	styleUrls: ['./classification-tree-component.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassificationTreeComponentComponent {
	treeControl = new FlatTreeControl<any>(
		node => node.level,
		node => node.expandable
	);

	treeFlattener = new MatTreeFlattener(
		this.transformer,
		node => node.level,
		node => node.expandable,
		node => node.children
	);

	dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
	@ContentChild(TemplateRef)
	templateRef: TemplateRef<TemplateRefPropsType | null>;
	selectedNode$ = new BehaviorSubject<FlatNode<IClassification> | null>(null);
	visibleNodes: FlatNode<IClassification>[] = [];
	@Input() expandAll: boolean;

	@Input() set data(classifications: IClassification[]) {
		this.dataSource.data = this.childBuilder(classifications);

		this.dataSource.data = this.dataSource.data.map((node, index) => {
			if (node.parentPk !== null) {
				return node;
			}
			return { ...node, index };
		});

		if (this.expandAll) {
			this.treeControl.expandAll();
		} else {
			this.treeControl.collapseAll();
		}
		this.updateVisibleNode();
	}

	@Output() nodeSelected = new EventEmitter<IClassification>();

	transformer(node: TreeData<IClassification>, level: number): FlatNode<IClassification> {
		return {
			...node,
			expandable: !!node.children && node.children.length > 0,
			level: level,
		};
	}

	childBuilder(nodes: readonly IClassification[]): TreeData<IClassification>[] {
		if (!nodes.length) return [];
		// Root level is null. Js interpritate null as string when used as key
		// More about it https://stackoverflow.com/a/9687890
		const result = this.aggregateBttChildren(nodes, {
			pk: null,
		} as IClassification);
		return result.children;
	}

	toggleNode(node: FlatNode<IClassification>) {
		if (this.treeControl.isExpanded(node)) {
			this.treeControl.collapse(node);
		} else {
			this.treeControl.expand(node);
		}
		this.updateVisibleNode();
	}
	@HostListener('window:keydown', ['$event'])
	keyboardNav(event: KeyboardEvent) {
		const currentIndex = this.visibleNodes.indexOf(this.selectedNode$.value);
		if (event.key === 'ArrowDown') {
			this.selectedNode$.next(
				this.visibleNodes[incrementIndex(currentIndex, this.visibleNodes.length)]
			);
		} else if (event.key === 'ArrowUp') {
			this.selectedNode$.next(
				this.visibleNodes[decrementIndex(currentIndex, this.visibleNodes.length)]
			);
		}
		if (event.key === 'Control') {
			this.toggleNode(this.selectedNode$.value);
		}
		this.nodeSelected.emit(this.selectedNode$.value);
	}
	updateVisibleNode() {
		this.visibleNodes = this.treeControl.dataNodes.filter(node => {
			let parent = this.getParentNode(node);
			while (parent) {
				if (!this.treeControl.isExpanded(parent)) {
					return false;
				}
				parent = this.getParentNode(parent);
			}
			return true;
		});
	}

	getParentNode(node: FlatNode<IClassification>): FlatNode<IClassification> | null {
		const nodeIndex = this.treeControl.dataNodes.indexOf(node);
		for (let i = nodeIndex - 1; i >= 0; i--) {
			if (this.treeControl.dataNodes[i].level < node.level) {
				return this.treeControl.dataNodes[i];
			}
		}
		return null;
	}

	private aggregateBttChildren(
		allNodes: readonly IClassification[],
		parent: IClassification
	): TreeData<IClassification> {
		const nodes = allNodes.filter(node => node.parentPk === parent.pk) || [];
		const children = [] as TreeData<IClassification>[];
		for (const child of nodes) {
			children.push(this.aggregateBttChildren(allNodes, child));
		}
		return {
			...parent,
			children,
		};
	}

	hasChild(_: number, node: { expandable: boolean }): boolean {
		return node.expandable;
	}
}

export function takeAllParentIdsNodes(
	nodePks: string[],
	allNodes: IClassification[]
): Set<string> {
	const parents: Set<string> = new Set<string>();
	while (nodePks.length) {
		const nodePk = nodePks.pop();
		if (!nodePk) continue;
		parents.add(nodePk);
		const node = allNodes.find(x => x.pk === nodePk);
		const nodeParentPk = node.parentPk;
		if (!nodeParentPk) continue;
		nodePks.push(nodeParentPk);
	}
	return parents;
}
